import { useEffect, useMemo } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { DCUITheme, DCUIThemeGeneral } from "@iddataweb/idw-common-ui/types";
import { FONT_OPTIONS } from "@constants/font-options";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { SelectOption } from "@idw-types/common";
import { loadFont } from "@iddataweb/idw-common-ui/utils";
import { SelectFormGroup } from "../../../../../common/select-form-group/select-form-group";
import { ColorPickerFormGroup } from "../../../../../common/color-picker-form-group/color-picker-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type DCUIThemingThemeGeneralSectionProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    dcuiTheme: DCUITheme;
    inheritedDCUITheme: DCUITheme;
    pageLevel: PageLevelTypes;
    updateDCUIThemePropertyValue: (value: DCUITheme) => void;
};

export const DCUIThemingThemeGeneralSection = (props: DCUIThemingThemeGeneralSectionProps) => {
    const { layout, labelWidth, isDisabled, dcuiTheme, inheritedDCUITheme, pageLevel, updateDCUIThemePropertyValue } = props;

    useEffect(() => {
        if (dcuiTheme.general.font) {
            loadFont(dcuiTheme.general.font, "dcui-font");
        }
    }, [dcuiTheme.general.font]);

    const handleThemeGeneralChange = (key: keyof DCUIThemeGeneral, value: string) => {
        const newPropertyValue: DCUITheme = {
            ...dcuiTheme,
            general: {
                ...dcuiTheme.general,
                [key]: value
            }
        };
        updateDCUIThemePropertyValue(newPropertyValue);
    };

    const handleFontChange = (newValue: SelectOption) => {
        handleThemeGeneralChange("font", newValue.label);
    };

    const handleResetFont = () => {
        handleThemeGeneralChange("font", inheritedDCUITheme.general.font);
    };

    const handlePrimaryButtonTextColorChange = (newValue: string) => {
        handleThemeGeneralChange("mainTextColor", newValue);
    };

    const handleResetPrimaryButtonTextColor = () => {
        handleThemeGeneralChange("mainTextColor", inheritedDCUITheme.general.mainTextColor);
    };

    const handlePrimaryButtonBackgroundColorChange = (newValue: string) => {
        handleThemeGeneralChange("mainColor", newValue)
    };

    const handleResetPrimaryButtonBackgroundColor = () => {
        handleThemeGeneralChange("mainColor", inheritedDCUITheme.general.mainColor);
    };

    const handleSecondaryButtonTextColorChange = (newValue: string) => {
        handleThemeGeneralChange("secondaryTextColor", newValue);
    };

    const handleResetSecondaryButtonTextColor = () => {
        handleThemeGeneralChange("secondaryTextColor", inheritedDCUITheme.general.secondaryTextColor);
    };

    const handleSecondaryButtonBackgroundColorChange = (newValue: string) => {
        handleThemeGeneralChange("secondaryColor", newValue);
    };

    const handleResetSecondaryButtonBackgroundColor = () => {
        handleThemeGeneralChange("secondaryColor", inheritedDCUITheme.general.secondaryColor);
    };

    const selectedFontOption = useMemo(() => {
        if (dcuiTheme.general.font) {
            return FONT_OPTIONS.find(fo => fo.label === dcuiTheme.general.font);
        }
        return null;
    }, [dcuiTheme.general.font]);

    return (
        <div>
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUITheme.general.font}
                currentThemingPropertyValue={dcuiTheme.general.font}
                pageLevel={pageLevel}
            >
                <SelectFormGroup
                    controlId={"dcui-theming-theme-font-select-input"}
                    automationId={"dcui-theming-theme-font-select-input"}
                    label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.general.inputs.font.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.general.inputs.font.label}
                    disabled={isDisabled}
                    isClearable={false}
                    isRequired={true}
                    isSearchable={true}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleFontChange}
                    options={FONT_OPTIONS}
                    selectedOption={selectedFontOption}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.general.inputs.font.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetFont}
                />
            </ThemingFormGroupWrapper>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUITheme.general.mainColor}
                currentThemingPropertyValue={dcuiTheme.general.mainColor}
                pageLevel={pageLevel}
            >
                <ColorPickerFormGroup
                    controlId={"dcui-theming-theme-primary-button-background-color-picker"}
                    automationId={"dcui-theming-theme-primary-button-background-color-picker"}
                    label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.general.inputs.primaryButtonBackgroundColor.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.general.inputs.primaryButtonBackgroundColor.label}
                    disabled={isDisabled}
                    isRequired={true}
                    layout={layout}
                    labelWidth={labelWidth}
                    value={dcuiTheme.general.mainColor || ""}
                    onChange={handlePrimaryButtonBackgroundColorChange}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.general.inputs.primaryButtonBackgroundColor.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetPrimaryButtonBackgroundColor}
                />

            </ThemingFormGroupWrapper>


            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUITheme.general.mainTextColor}
                currentThemingPropertyValue={dcuiTheme.general.mainTextColor}
                pageLevel={pageLevel}
            >
                <ColorPickerFormGroup
                    controlId={"dcui-theming-theme-primary-button-text-color-picker"}
                    automationId={"dcui-theming-theme-primary-button-text-color-picker"}
                    label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.general.inputs.primaryButtonTextColor.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.general.inputs.primaryButtonTextColor.label}
                    disabled={isDisabled}
                    isRequired={true}
                    layout={layout}
                    labelWidth={labelWidth}
                    value={dcuiTheme.general.mainTextColor || ""}
                    onChange={handlePrimaryButtonTextColorChange}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.general.inputs.primaryButtonTextColor.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetPrimaryButtonTextColor}
                />

            </ThemingFormGroupWrapper>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUITheme.general.secondaryColor}
                currentThemingPropertyValue={dcuiTheme.general.secondaryColor}
                pageLevel={pageLevel}
            >
                <ColorPickerFormGroup
                    controlId={"dcui-theming-theme-secondary-button-background-color-picker"}
                    automationId={"dcui-theming-theme-secondary-button-background-color-picker"}
                    label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.general.inputs.secondaryButtonBackgroundColor.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.general.inputs.secondaryButtonBackgroundColor.label}
                    disabled={isDisabled}
                    isRequired={true}
                    layout={layout}
                    labelWidth={labelWidth}
                    value={dcuiTheme.general.secondaryColor || ""}
                    onChange={handleSecondaryButtonBackgroundColorChange}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.general.inputs.secondaryButtonBackgroundColor.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetSecondaryButtonBackgroundColor}
                />

            </ThemingFormGroupWrapper>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUITheme.general.secondaryTextColor}
                currentThemingPropertyValue={dcuiTheme.general.secondaryTextColor}
                pageLevel={pageLevel}
            >
                <ColorPickerFormGroup
                    controlId={"dcui-theming-theme-secondary-button-text-color-picker"}
                    automationId={"dcui-theming-theme-secondary-button-text-color-picker"}
                    label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.general.inputs.secondaryButtonTextColor.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.general.inputs.secondaryButtonTextColor.label}
                    disabled={isDisabled}
                    isRequired={true}
                    layout={layout}
                    labelWidth={labelWidth}
                    value={dcuiTheme.general.secondaryTextColor || ""}
                    onChange={handleSecondaryButtonTextColorChange}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.general.inputs.secondaryButtonTextColor.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetSecondaryButtonTextColor}
                />

            </ThemingFormGroupWrapper>
        </div>
    );
};